import { DPD, DPD_LOCAL } from "../../constants/strings";
import { isDpdLocalDomain } from "../app/app.modes";
import {
  DPD_BUSINESS_ID,
  DPD_LOCAL_BUSINESS_ID,
} from "../userDetailsForm/accountDetails/accountDetails.constants";
import {
  BULK_ACCOUNTS_FIELD,
  CHAT_QUEUE_FORM_ERRORS,
  CHAT_QUEUE_TYPES,
  MAX_BULK_CHAT_QUEUE_ACCOUNTS,
  QUEUE_TYPE_FIELD,
  QUEUE_TYPES,
  SEARCH_FORM_ERRORS,
  SPECIAL_SERVICE_EXCLUSIONS_FIELD,
} from "./customerSettings.constants";

const CSV_MIME_TYPES = ["text/csv", "application/csv"];

const uniqueValues = (values) =>
  values.filter((value, index) => values.indexOf(value) === index);

export const getInitialCustomerSettingsSearchFormValues = () => ({
  businessId: isDpdLocalDomain() ? DPD_LOCAL_BUSINESS_ID : DPD_BUSINESS_ID,
  account: "",
});

export const getInitialCustomerSettingsFormValues = () => ({
  [SPECIAL_SERVICE_EXCLUSIONS_FIELD]: [],
});

export const getInitialChatQueueFormValues = () => ({
  [QUEUE_TYPE_FIELD]: QUEUE_TYPES.NONE,
});

export const getInitialBulkChatQueueFormValues = () => ({
  [QUEUE_TYPE_FIELD]: QUEUE_TYPES.NONE,
  [BULK_ACCOUNTS_FIELD]: [],
});

export const validateSearchFormValues = ({ account = "" }) => {
  const errors = {};

  if (account !== "0" && ![6, 7].includes(account.length)) {
    errors.account = SEARCH_FORM_ERRORS.MISSING_ACCOUNT;
  }

  return errors;
};

export const isBulkAccountNumber = (account = "") => /^\d{6,7}$/.test(account);

export const parseBulkAccountNumbers = (value = "") => {
  const values = Array.isArray(value) ? value : String(value).split(/[\s,;]+/);

  return uniqueValues(
    values.map((item) => String(item).trim()).filter(Boolean)
  );
};

export const mergeBulkAccountNumbers = (
  existing = [],
  incoming,
  max = MAX_BULK_CHAT_QUEUE_ACCOUNTS
) =>
  parseBulkAccountNumbers([
    ...existing,
    ...parseBulkAccountNumbers(incoming),
  ]).slice(0, max);

export const isCsvFile = (file) =>
  !!file &&
  (String(file.name || "")
    .toLowerCase()
    .endsWith(".csv") ||
    CSV_MIME_TYPES.includes(String(file.type || "").toLowerCase()));

const toQueueType = (value = "") =>
  CHAT_QUEUE_TYPES.find(
    (queueType) =>
      queueType.toLowerCase() === String(value).trim().toLowerCase()
  ) || QUEUE_TYPES.NONE;

const parseCsvRows = (text = "") =>
  String(text)
    .replace(/^\uFEFF/, "")
    .split(/[\r\n]+/)
    .map((line) =>
      line
        .replace(/"/g, "")
        .split(/[\s,;\t]+/)
        .filter(Boolean)
    )
    .filter((cells) => cells.length);

export const parseBulkChatQueueCsv = (text = "") => {
  const items = [];

  parseCsvRows(text).forEach((cells) => {
    const queueType = cells.map(toQueueType).find(Boolean) || QUEUE_TYPES.NONE;

    cells.filter(isBulkAccountNumber).forEach((account) => {
      const existing = items.find((item) => item.account === account);

      if (existing) {
        existing.queueType = queueType;
      } else {
        items.push({ account, queueType });
      }
    });
  });

  const queueTypes = uniqueValues(
    items.map((item) => item.queueType).filter(Boolean)
  );

  return {
    accounts: items.map((item) => item.account),
    items,
    queueType: queueTypes.length === 1 ? queueTypes[0] : QUEUE_TYPES.NONE,
  };
};

export const groupBulkChatQueueItems = (items = [], fallbackQueueType = "") => {
  const groups = [];

  items.forEach(({ account, queueType }) => {
    const groupQueueType = queueType || fallbackQueueType;

    if (!account || !groupQueueType) {
      return;
    }

    const group = groups.find((item) => item.queueType === groupQueueType);

    if (group) {
      group.accounts.push(account);
    } else {
      groups.push({ queueType: groupQueueType, accounts: [account] });
    }
  });

  return groups;
};

export const validateBulkChatQueueFormValues = ({
  queueType = "",
  accounts = [],
}) => {
  const errors = {};
  const parsedAccounts = parseBulkAccountNumbers(accounts);

  if (!queueType) {
    errors[QUEUE_TYPE_FIELD] = CHAT_QUEUE_FORM_ERRORS.MISSING_QUEUE_TYPE;
  }

  if (!parsedAccounts.length) {
    errors[BULK_ACCOUNTS_FIELD] = CHAT_QUEUE_FORM_ERRORS.MISSING_ACCOUNTS;
  } else if (parsedAccounts.some((account) => !isBulkAccountNumber(account))) {
    errors[BULK_ACCOUNTS_FIELD] = CHAT_QUEUE_FORM_ERRORS.INVALID_ACCOUNTS;
  } else if (parsedAccounts.length > MAX_BULK_CHAT_QUEUE_ACCOUNTS) {
    errors[BULK_ACCOUNTS_FIELD] = CHAT_QUEUE_FORM_ERRORS.TOO_MANY_ACCOUNTS;
  }

  return errors;
};

export const customerCustomerSettingsFromForms = (
  searchForm,
  customerSettingsForm
) => ({
  ...searchForm,
  ...customerSettingsForm,
  businessId: searchForm.businessId.toString(),
});

export const prepareChatQueueTableData = (items = []) =>
  items.map((item) => ({
    id: `${item.account}-${item.businessId}`,
    values: item,
    fields: [
      item.account,
      String(item.businessId) === String(DPD_LOCAL_BUSINESS_ID)
        ? DPD_LOCAL
        : DPD,
      item.queueType,
    ],
  }));
